import { TFunction } from '@sortlist-frontend/translation/server-only';
import dynamic from 'next/dynamic';
import { Fragment, ReactElement, useRef, useState } from 'react';

import { IconSprite } from '_components/IconSprite';
import { NavigationData } from '_components/layout/Layout';
import { Expertise } from '_core/repos/expertises.repo';
import { Agencies, Agency, CardsSectionOptions, Page, PagesAgency } from '_types/public-api';

import { AgencyCard } from './AgencyCard';
import { NoFilterResultsState } from './NoFilterResultsState';

const TurnstileModal = dynamic(() => import('./AgencyCard/components').then((mod) => mod.TurnstileModal), {
  ssr: false,
});

type AgenciesPanelProps = {
  className?: string;
  organicAgencies?: Agencies;
  paidAgencies?: Agencies;
  page: Page;
  t: TFunction;
  divider?: ReactElement;
  isSeaPage?: boolean;
  navigationData: NavigationData;
  agencyCardsLayout?: string;
  appRouter?: boolean;
  expertises?: Expertise[];
};

export const getPaidAgenciesSectionLimit = (cardsOptions: CardsSectionOptions) => {
  return cardsOptions?.paid_providers_limit ?? 6;
};

const getAgenciesList = (isSeaPage: boolean, agencies: Agency[], paidAgencies: Agency[]) => {
  if (!isSeaPage) {
    return agencies ?? [];
  }

  const paidAgenciesList = paidAgencies ?? [];
  const organicAgenciesList =
    agencies?.filter((agency) => paidAgencies?.find((paidAgency) => paidAgency.id === agency.id) == null) ?? [];

  return [...paidAgenciesList, ...organicAgenciesList].slice(0, 20);
};

const getPagesAgenciesList = (isSeaPage: boolean, agencies: PagesAgency[], paidAgencies: PagesAgency[]) => {
  if (!isSeaPage) {
    return agencies ?? [];
  }

  const paidAgenciesList = paidAgencies ?? [];
  const organicAgenciesList =
    agencies?.filter(
      (agency) =>
        paidAgencies?.find(
          (paidAgency) => paidAgency?.relationships?.agency?.data?.id === agency.relationships?.agency?.data?.id,
        ) == null,
    ) ?? [];

  return [...paidAgenciesList, ...organicAgenciesList].slice(0, 20);
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const AgenciesPanel = (props: AgenciesPanelProps) => {
  const {
    organicAgencies: organicAgenciesProp,
    paidAgencies: paidAgenciesProp,
    page,
    t,
    divider,
    isSeaPage,
    navigationData,
    appRouter = false,
    agencyCardsLayout,
    expertises,
  } = props;

  const [showTurnstileModal, setShowTurnstileModal] = useState(false);
  const [isTurnstileVerified, setIsTurnstileVerified] = useState(false);
  const [clickCountPerSlug, setClickCounter] = useState<{ [key: string]: number }>({});
  const [agencySlug, setAgencySlug] = useState('');

  const { noFiltersResults } = navigationData;

  const ref = useRef<HTMLDivElement>(null);
  const defaultHorizontalDisplay = page?.data?.attributes?.page_options?.cards?.layout === 'horizontal';
  const horizontalDisplay = agencyCardsLayout === 'horizontal';
  const fullWidthLayout = defaultHorizontalDisplay || horizontalDisplay;
  const isPaidSectionList = defaultHorizontalDisplay;

  const {
    data: agencies,
    included,
    meta: { pagination },
  } = organicAgenciesProp || { meta: {} };

  const { data: paidAgencies, included: inclduedPaid } = paidAgenciesProp || { meta: {} };

  const includedAgencies =
    included &&
    included.map((obj) => {
      if (obj.attributes.team_size === null) return { ...obj, attributes: { ...obj.attributes, team_size: 0 } };
      return obj;
    });

  const includedPaidAgencies =
    inclduedPaid &&
    inclduedPaid.map((obj) => {
      if (obj.attributes.team_size === null) return { ...obj, attributes: { ...obj.attributes, team_size: 0 } };
      return obj;
    });

  const hasPaidAgencies = paidAgencies && paidAgencies.length > 0;

  const agenciesList = getAgenciesList(!!isSeaPage, includedAgencies ?? [], inclduedPaid ?? []);
  const pagesAgenciesList = getPagesAgenciesList(!!isSeaPage, agencies ?? [], paidAgencies ?? []);

  const paidAgenciesSectionLimit = getPaidAgenciesSectionLimit(page?.data?.attributes?.page_options?.cards ?? {});
  const hasNoOrganicResults = agenciesList != null && agenciesList.length === 0;

  const hasNoFilterResults = hasNoOrganicResults && Boolean(noFiltersResults);

  const paidAgenciesList = includedPaidAgencies?.slice(0, paidAgenciesSectionLimit);
  const CLICKS_BEFORE_VERIFICATION = 3;

  const onAgencyCardClicked = (slug: string) => {
    if (isTurnstileVerified) return;

    const previousClicksCount = clickCountPerSlug[slug] ?? 0;
    const clicksCount = previousClicksCount + 1;

    setClickCounter({ ...clickCountPerSlug, [slug]: clicksCount });

    if (clicksCount >= CLICKS_BEFORE_VERIFICATION) {
      setAgencySlug(slug);
      setShowTurnstileModal(true);
    }
  };

  return (
    <Fragment>
      <IconSprite />
      {hasNoFilterResults ? <NoFilterResultsState /> : null}

      {!isSeaPage && hasPaidAgencies ? (
        <div
          className={`container-lg pt-6 ${isPaidSectionList ? 'agencies-horizontal-layout' : 'agencies-card-layout'}`}>
          <div className={'pb-4'}>
            <h2 className="medium p">{page?.data.attributes.title.featured_title}</h2>
          </div>
          <ul className="m-0 p-0 gap-x-4 gap-y-4 grid-list">
            {paidAgenciesList?.map((agency, index) => (
              <AgencyCard
                appRouter={appRouter}
                data={paidAgencies[index]}
                agency={agency}
                key={agency.id}
                relativePosition={index + 1}
                absolutePosition={index + 1}
                page={page}
                t={t}
                navigationData={navigationData}
                paid
                expertises={expertises}
                onCardClick={onAgencyCardClicked}
              />
            ))}
          </ul>
        </div>
      ) : null}
      {agencies == null || hasNoFilterResults ? null : (
        <div
          ref={ref}
          id="content"
          className={`container-lg relative pt-6 ${fullWidthLayout ? 'agencies-horizontal-layout' : 'agencies-card-layout'}`}>
          {divider}
          <ul className="m-0 p-0 gap-x-4 gap-y-4 grid-list">
            {agenciesList.map((agency, index) => (
              <AgencyCard
                appRouter={appRouter}
                data={
                  pagesAgenciesList.find(
                    (pageAgency) => pageAgency?.relationships?.agency?.data?.id === agency.id,
                  ) as PagesAgency
                }
                agency={agency}
                key={agency.id}
                relativePosition={index + 1}
                absolutePosition={(paidAgenciesList?.length ?? 0) + index + 1}
                page={page}
                t={t}
                navigationData={navigationData}
                paid={isSeaPage && paidAgencies != null && index < paidAgencies?.length}
                expertises={expertises}
                onCardClick={onAgencyCardClicked}
              />
            ))}
          </ul>
        </div>
      )}
      {showTurnstileModal && (
        <TurnstileModal
          onClose={() => setShowTurnstileModal(false)}
          onVerified={() => {
            setIsTurnstileVerified(true);
            setShowTurnstileModal(false);
          }}
          agencySlug={agencySlug}
          path={page?.data.attributes.path}
        />
      )}
    </Fragment>
  );
};
